import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Observable, ReplaySubject, defer, from, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, scan, shareReplay, startWith, switchMap } from 'rxjs/operators';

import { ParametersService } from '@pathe-payment/common';
import { IVouchersService, VoucherModel } from '@pathe-payment/core';
import { Parametres } from 'src/environments/parametres';

export const BATCH_CONCURRENCY = 5;
const STORAGE_KEY = 'ppa:voucher-batch-codes';

export interface VoucherWithStatus {
  code: string;
  found: boolean;
  voucher?: VoucherModel;
}

export interface VoucherBatchSearchCompletion {
  searchId: number;
  missingCodes: string[];
}

type SearchEvent = { type: 'start'; total: number } | { type: 'result'; item: VoucherWithStatus } | { type: 'done' };

interface BatchState {
  loading: boolean;
  missingCodes: string[];
  processed: number;
  results: VoucherWithStatus[];
  searchId: number;
  total: number;
}

const INITIAL_STATE: BatchState = { loading: false, missingCodes: [], processed: 0, results: [], searchId: 0, total: 0 };

@Injectable({ providedIn: 'root' })
export class VoucherBatchSearchService {
  readonly loading$: Observable<boolean>;
  readonly progress$: Observable<{ processed: number; total: number }>;
  readonly results$: Observable<VoucherWithStatus[]>;
  readonly completedSearch$: Observable<VoucherBatchSearchCompletion>;

  private readonly codes$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  private readonly destroyRef = inject(DestroyRef);
  private readonly parametersService = inject(ParametersService);
  private readonly pays = this.parametersService.get(Parametres.PAYS) as string;
  private readonly vouchersService = inject(IVouchersService);

  constructor() {
    const state$ = this.initPipeline();
    this.loading$ = state$.pipe(map((s) => s.loading));
    this.progress$ = state$.pipe(map((s) => ({ processed: s.processed, total: s.total })));
    this.results$ = state$.pipe(map((s) => s.results));
    this.completedSearch$ = state$.pipe(
      filter((state) => !state.loading && state.total > 0),
      map((state) => ({ searchId: state.searchId, missingCodes: state.missingCodes })),
      distinctUntilChanged((previous, current) => previous.searchId === current.searchId),
    );
  }

  hasPersistedCodes(): boolean {
    return !!this.readPersistedCodes();
  }

  resumeFromStorage(): boolean {
    const codes = this.readPersistedCodes();
    if (!codes || codes.length === 0) return false;
    this.codes$.next(codes);
    return true;
  }

  startSearch(codes: string[]): void {
    this.persistCodes(codes);
    this.codes$.next(codes);
  }

  private initPipeline(): Observable<BatchState> {
    return this.codes$.pipe(
      switchMap((codes) => this.runBatch(codes)),
      scan((state, event): BatchState => {
        switch (event.type) {
          case 'start':
            return { loading: true, missingCodes: [], processed: 0, results: [], searchId: state.searchId + 1, total: event.total };
          case 'result':
            return {
              ...state,
              missingCodes: event.item.found ? state.missingCodes : [...state.missingCodes, event.item.code],
              processed: state.processed + 1,
              results: [...state.results, event.item],
            };
          case 'done':
            return { ...state, loading: false };
        }
      }, INITIAL_STATE),
      startWith(INITIAL_STATE),
      takeUntilDestroyed(this.destroyRef),
      shareReplay(1),
    );
  }

  private persistCodes(codes: string[]): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
    } catch {
      // sessionStorage unavailable (private mode, quota) — in-memory flow still works for current session.
    }
  }

  private readPersistedCodes(): string[] | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) && parsed.every((c) => typeof c === 'string') ? parsed : null;
    } catch {
      return null;
    }
  }

  private runBatch(codes: string[]): Observable<SearchEvent> {
    return new Observable<SearchEvent>((observer) => {
      observer.next({ type: 'start', total: codes.length });

      const sub = from(codes)
        .pipe(
          mergeMap(
            (code) =>
              defer(() => this.vouchersService.getVoucher(this.pays, code, true)).pipe(
                map((voucher) => ({ code, found: true, voucher })),
                catchError(() => of({ code, found: false } as VoucherWithStatus)),
              ),
            BATCH_CONCURRENCY,
          ),
        )
        .subscribe({
          next: (item) => observer.next({ type: 'result', item }),
          error: (err) => observer.error(err),
          complete: () => {
            observer.next({ type: 'done' });
            observer.complete();
          },
        });

      return () => sub.unsubscribe();
    });
  }
}
